import type { ACCEPTED_USER_PROPERTIES } from 'constants/user';
import { TIMELINE_FILTERS_TO_REMEMBER, USER_PROPERTY_TRACKING_BLACKLIST } from 'constants/user';
import type { XhrStatus } from 'redux/reusable/xhrStatus';
import { createXhrStatusModule } from 'redux/reusable/xhrStatus';
import type { User } from 'types/user';
import { actionCreatorFactory } from 'typescript-fsa';

export const userXhrStatusModule = createXhrStatusModule('user');
export interface UserPropertyRaw {
  user_id: number;
  property: string;
  value: string;
}

export type UserPropertiesRaw = Array<UserPropertyRaw>;

export enum UserPermission {
  Create = 'create',
  Read = 'read',
  Update = 'update',
  Delete = 'delete',
  Notify = 'notify',
  ChangeOwnership = 'change_ownership',
  ManageSubscriptions = 'manage_subscriptions',
  ManageDev = 'manage_dev',
  ManagePlan = 'manage_plan',
  ContactManager = 'contact_manager',
  ContactAdmin = 'contact_admin',
}

export const EntityLevelPermissions: Array<UserPermission> = [
  UserPermission.Create,
  UserPermission.Read,
  UserPermission.Update,
  UserPermission.Delete,
];

export const TaskLevelPermissionNames: Record<string, string> = {
  [UserPermission.Notify]: 'Notify user',
  [UserPermission.ChangeOwnership]: 'Change ownership',
  [UserPermission.ManageSubscriptions]: 'Manage subscription',
  [UserPermission.ManageDev]: 'Manage dev',
  [UserPermission.ManagePlan]: 'Manage plan',
  [UserPermission.ContactAdmin]: 'Contact admin',
  [UserPermission.ContactManager]: 'Contact manager',
} as const;

export enum UserPermissionResource {
  Account = 'account',
  Client = 'company',
  Project = 'project',
  Report = 'report',
  Tag = 'label',
  Team = 'team',
  User = 'user',
  Forecast = 'forecast',
  Invoice = 'invoice',
  DayProperty = 'day_property',
}

/**
 * This maps the backend resource names to more human-friendly properties.
 */
export const UserPermissionResourceAlias = {
  [UserPermissionResource.Account]: 'workspace',
  [UserPermissionResource.Client]: 'clients',
  [UserPermissionResource.Project]: 'projects',
  [UserPermissionResource.Report]: 'reports',
  [UserPermissionResource.Tag]: 'tags',
  [UserPermissionResource.Team]: 'teams',
  [UserPermissionResource.User]: 'users',
  [UserPermissionResource.Forecast]: 'forecasts',
  [UserPermissionResource.Invoice]: 'invoices',
  [UserPermissionResource.DayProperty]: 'day_property',
} as const;

export type UserPermissions = Record<UserPermissionResource, Array<UserPermission>>;
export type UserGlobalPermissions = Record<UserPermissionResource, Array<UserPermission>>;

export type UserPermissionAccessor = Record<
  (typeof UserPermissionResourceAlias)[UserPermissionResource],
  boolean
>;

export interface UserPermissionsFacade {
  canCreate: UserPermissionAccessor;
  canRead: UserPermissionAccessor;
  canUpdate: UserPermissionAccessor;
  canDelete: UserPermissionAccessor;
  canNotify: Pick<UserPermissionAccessor, 'users'>;
  canChangeOwnershipOf: Pick<UserPermissionAccessor, 'workspace'>;
  canManageSubscriptions: boolean;
  canManageOauthApplications: boolean;
  canUpgradeSubscription: boolean;
  canContactAdmin: boolean;
  canContactManager: boolean;
  canLockDays: boolean;
  canUnlockDays: boolean;
}

export interface UserState extends User {
  force_tour?: boolean;
  accountSettings: {
    id?: number;
    hours_suggestable?: boolean;
    memory_suggestions?: boolean;
  };
  permissions: UserPermissions;
  globalPermissions: UserGlobalPermissions;
  emulatedPermissions?: UserPermissions | null;
  emulatedRoleId?: number | null;
  showDevtools: boolean;
  xhrStatus: XhrStatus;
}

export type UserPropertyValue = string | boolean | number | Array<string> | null | undefined;

// Derive proper types from arrays of constants:
export type AllAcceptedUserPropertyKey = (typeof ACCEPTED_USER_PROPERTIES)[number];
export type BlacklistUserPropertyKey = (typeof USER_PROPERTY_TRACKING_BLACKLIST)[number];
export type CustomEventNameUserPropertyKey = Exclude<
  AllAcceptedUserPropertyKey,
  BlacklistUserPropertyKey
>;
export type TimelineFilterToRemember = (typeof TIMELINE_FILTERS_TO_REMEMBER)[number];

export const isTimelineFilterToRemember = (key: string): boolean => {
  return (TIMELINE_FILTERS_TO_REMEMBER as ReadonlyArray<string>).includes(key);
};

export const isUserPropertyInBlacklist = (key: AllAcceptedUserPropertyKey): boolean => {
  return (USER_PROPERTY_TRACKING_BLACKLIST as ReadonlyArray<string>).includes(key);
};

export type UserPropertyEventDictionary = Partial<{
  [_key in CustomEventNameUserPropertyKey]: string;
}>;

export type UserProperties = Partial<{ [_key in AllAcceptedUserPropertyKey]: UserPropertyValue }>;

export interface UserProperty {
  user_id: number;
  property: string;
  value: UserPropertyValue;
}

const actionCreator = actionCreatorFactory('timely/user');

export const fetchedUser = actionCreator<User>('FETCHED');
export const setUserProperties = actionCreator<UserProperties>('SET_USER_PROPERTIES');
export const setUserProperty = actionCreator<Partial<UserProperties>>('SET_USER_PROPERTY');
export const savedUser = actionCreator<User>('SAVED');
export const setUserAccountSettings = actionCreator<UserState['accountSettings']>(
  'SET_USER_ACCOUNT_SETTINGS'
);
export const setUserPermissions = actionCreator<UserPermissions>('SET_USER_PERMISSIONS');
export const setUserGlobalPermissions = actionCreator<UserGlobalPermissions>(
  'SET_USER_GLOBAL_PERMISSIONS'
);
export const restartTour = actionCreator('RESTART_TOUR');
export const clearTour = actionCreator('CLEAR_TOUR');
export const emulatePermissions = actionCreator<UserPermissions>('EMULATE_PERMISSIONS');
export const stopEmulatingPermissions = actionCreator('STOP_EMULATING_PERMISSIONS');
export const setShowDevTools = actionCreator<boolean>('TOGGLE_DEV_TOOLS');
export const emulateRole = actionCreator<number>('EMULATE_ROLE');
export const stopEmulatingRole = actionCreator('STOP_EMULATING_ROLE');
